import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    BoltChangePhysicalPropertiesDto,
    RecordManualMeasurementDto,
} from '../../_generated/api/tagged-hardware'
import { useArchiveContext } from './ArchiveContext'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useProvideContext } from '../../hooks/context'
import { useToastContext } from './ToastContext'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { useAction } from '../../hooks/action'
import {
    mapModel,
    mapReadTableResponse,
    mapReadResponse,
    throwIfResponseNotOk,
    ok,
} from '../../tools/api'
import Bolt from '../../models/Bolt'
import BoltConnection from '../../models/BoltConnection'
import TableControl from '../../models/TableControl'
import {
    unpackManufacturedBoltsTableSpec,
    unpackUnassignedBoltsTableSpec,
} from '../table/table-spec'
import Measurement from '../../models/Measurement'
import Sensor from '../../models/Sensor'
import Device from '../../models/Device'
import { useMutableObject } from '../../hooks/mutable'

export interface BoltContextValue extends DataContextValue {
    selectedBolt?: Bolt
    selectedBoltParent?: BoltConnection
    updateBolt: (
        bolt: Bolt,
        change: BoltChangePhysicalPropertiesDto
    ) => Promise<boolean | undefined>
    archiveBolt: (boltId?: string) => Promise<void>
    restoreBolt: (boltId?: string) => Promise<void>
    archiveMeasurement: (boltId?: string, measurementId?: string) => Promise<void>
    restoreMeasurement: (boltId?: string, measurementId?: string) => Promise<void>

    selectedMeasurement?: Measurement
    setSelectedMeasurement: (measurement?: Measurement) => void
    measurementSensor?: Sensor
    measurementDevice?: Device

    unassignedBolts: TableControl<Bolt>
    manufacturedBolts: TableControl<Bolt>
    createManualMeasurement: (bolt: Bolt, measurement: RecordManualMeasurementDto) => Promise<void>
    updateMeasurementComment: (
        bolt: Bolt,
        measurement: Measurement,
        comment: string
    ) => Promise<boolean | undefined>
}

const BoltContext = createContext<BoltContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const { boltId } = useParams()
    const { boltApi, boltConnectionApi, sensorApi, deviceApi } = useApiContext()
    const { includeArchived } = useArchiveContext()
    const { selectedOrganization, lazySelectedOrganizationId } = useUserContext()
    const Toast = useToastContext()
    const [selectedMeasurement, setSelectedMeasurement] = useState<Measurement>()
    const [measurementSensor, setMeasurementSensor] = useState<Sensor>()
    const [measurementDevice, setMeasurementDevice] = useState<Device>()

    const [selectedBolt, selectedLoading, selectedError, refreshSelectedBolt, replaceSelectedBolt] =
        useMutableObject(async () => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltId)) return
            const response = await boltApi.getBoltV2({
                boltId,
                tenantId: orgId,
                includeArchived: true,
            })
            return mapReadResponse(response, Bolt)
        }, [boltId, selectedOrganization?.id])

    useEffect(() => {
        const clearBoltRelations = () => {
            setMeasurementSensor(undefined)
            setMeasurementDevice(undefined)
        }
        clearBoltRelations()
        ;(async () => {
            const orgId = selectedOrganization?.id
            if (!(orgId && selectedMeasurement)) return

            const { sensorId, deviceId } = selectedMeasurement

            const sensorResponse = await sensorApi.getSensorV2({
                sensorId,
                tenantId: orgId,
            })
            if (ok(sensorResponse)) setMeasurementSensor(new Sensor(sensorResponse.data))

            const deviceResponse = await deviceApi.getDeviceV2({
                deviceId,
                tenantId: orgId,
            })
            if (ok(deviceResponse)) setMeasurementDevice(new Device(deviceResponse.data))

            return clearBoltRelations
        })()
    }, [selectedMeasurement, selectedOrganization?.id])

    const [selectedBoltParent, parentLoading, parentError] = useImmutableObject(async () => {
        const parentId = selectedBolt?.assignment?.groupId
        const orgId = selectedOrganization?.id
        if (!(orgId && parentId)) return null
        const response = await boltConnectionApi.getBoltConnectionV3({
            boltConnectionId: parentId,
            tenantId: orgId,
        })
        throwIfResponseNotOk(response)
        return mapModel(response.data, BoltConnection)
    }, [selectedBolt])

    const [unassignedBolts, loadingUnassignedBolts, unassignedBoltsError, refreshUnassignedBolts] =
        usePaginatedTable(
            async (table: TableControl<Bolt>) => {
                const orgId = selectedOrganization?.id
                if (!orgId) return null
                const { diameter, type, material, nominalLength, createdAt } =
                    unpackUnassignedBoltsTableSpec(table)
                const response = await boltApi.getUnassignedBoltsV2({
                    tenantId: orgId,
                    sortBy: table.sortBy,
                    ascending: table.ascending,
                    createdAtStart: createdAt?.[0]?.toISOString(),
                    createdAtEnd: createdAt?.[1]?.toISOString(),
                    nominalLengthStart: nominalLength?.[0],
                    nominalLengthEnd: nominalLength?.[1],
                    boltDiameter: diameter?.[0],
                    material: material?.[0],
                    boltType: type?.[0],
                    skip: table.skip,
                    take: table.take,
                    includeArchived,
                    includeMeasurements: true,
                })
                // NOTE: Do not attempt to throw if failing to get unassigned bolts.
                return mapReadTableResponse(response, table, Bolt)
            },
            [selectedOrganization?.id, includeArchived]
        )

    const [
        manufacturedBolts,
        _loadingManufacturedBolts,
        _manufacturedBoltsError,
        refreshManufacturedBolts,
    ] = usePaginatedTable(
        async (table: TableControl<Bolt>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null

            const { diameter, type, material, nominalLength, createdAt } =
                unpackManufacturedBoltsTableSpec(table)

            const response = await boltApi.getBoltsOfManufacturerV2({
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                createdAtStart: createdAt?.[0]?.toISOString(),
                createdAtEnd: createdAt?.[1]?.toISOString(),
                nominalLengthStart: nominalLength?.[0],
                nominalLengthEnd: nominalLength?.[1],
                boltDiameter: diameter?.[0],
                material: material?.[0],
                boltType: type?.[0],
                skip: table.skip,
                take: table.take,
                includeArchived,
                includeMeasurements: true,
            })
            // NOTE: Do not attempt to throw if failing to get manufactured bolts.
            return mapReadTableResponse(response, table, Bolt)
        },
        [selectedOrganization?.id, includeArchived]
    )

    const [updateLoading, updateError, updateBolt] = useAction(
        async (bolt: Bolt, change: BoltChangePhysicalPropertiesDto) => {
            const response = await boltApi.changePhysicalPropertiesOnBoltV2({
                boltId: bolt.id,
                tenantId: lazySelectedOrganizationId,
                boltChangePhysicalPropertiesDto: change,
            })
            throwIfResponseNotOk(response)
            return true
        }
    )

    const [archiveLoading, archiveError, archiveBolt] = useAction(async (boltId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && boltId)) return
        const response = await boltApi.archiveBoltV2({ boltId, tenantId: orgId })
        throwIfResponseNotOk(response)
        refreshSelectedBolt()
        refreshUnassignedBolts()
        refreshManufacturedBolts()
        Toast.completed('archive', 'bolt')
    })

    const [restoreLoading, restoreError, restoreBolt] = useAction(async (boltId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && boltId)) return
        const response = await boltApi.restoreBoltV2({ boltId, tenantId: orgId })
        throwIfResponseNotOk(response)
        refreshSelectedBolt()
        refreshUnassignedBolts()
        refreshManufacturedBolts()
        Toast.completed('restore', 'bolt')
    })

    const [measurementArchiveLoading, measurementArchiveError, archiveMeasurement] = useAction(
        async (boltId?: string, measurementId?: string) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltId && measurementId)) return
            const response = await boltApi.archiveMeasurementV2({
                boltId,
                measurementId,
                tenantId: orgId,
            })
            throwIfResponseNotOk(response)
            refreshSelectedBolt()
            refreshUnassignedBolts()
            refreshManufacturedBolts()
            Toast.completed('archive', 'measurement')
        }
    )

    const [measurementRestoreLoading, measurementRestoreError, restoreMeasurement] = useAction(
        async (boltId?: string, measurementId?: string) => {
            const orgId = selectedOrganization?.id
            if (!(orgId && boltId && measurementId)) return
            const response = await boltApi.restoreMeasurementV2({
                boltId,
                measurementId,
                tenantId: orgId,
            })
            throwIfResponseNotOk(response)
            refreshSelectedBolt()
            refreshUnassignedBolts()
            refreshManufacturedBolts()
            Toast.completed('restore', 'measurement')
        }
    )

    const [manualLoading, manualError, createManualMeasurement] = useAction(
        async (bolt: Bolt, measurement: RecordManualMeasurementDto) => {
            const response = await boltApi.recordManualBoltMeasurementV2({
                boltId: bolt.id,
                tenantId: lazySelectedOrganizationId,
                recordManualMeasurementDto: measurement,
            })
            throwIfResponseNotOk(response)
            refreshSelectedBolt()
            const updatedBolt = mapReadResponse(response, Bolt)
            replaceSelectedBolt(updatedBolt)
        }
    )

    const [measurementCommentLoading, measurementCommentError, updateMeasurementComment] =
        useAction(async (bolt: Bolt, measurement: Measurement, comment: string) => {
            const response = await boltApi.changeMeasurementCommentV2({
                boltId: bolt.id,
                measurementId: measurement.id,
                tenantId: lazySelectedOrganizationId,
                setMeasurementCommentDto: { comment },
            })
            throwIfResponseNotOk(response)
            refreshSelectedBolt()
            return true
        })

    const loading =
        selectedLoading ||
        archiveLoading ||
        restoreLoading ||
        measurementArchiveLoading ||
        measurementRestoreLoading ||
        measurementCommentLoading
    const error =
        selectedError ||
        archiveError ||
        restoreError ||
        measurementArchiveError ||
        measurementRestoreError ||
        measurementCommentError

    useEffect(() => {
        if (error) Toast.error(error, 'Bolt error')
    }, [error])

    const value: BoltContextValue = {
        loading,
        error,

        selectedBolt,
        selectedBoltParent,
        updateBolt,
        archiveBolt,
        restoreBolt,
        archiveMeasurement,
        restoreMeasurement,

        selectedMeasurement,
        setSelectedMeasurement,
        measurementSensor,
        measurementDevice,
        createManualMeasurement,
        updateMeasurementComment,

        unassignedBolts,
        manufacturedBolts,
    }

    return <BoltContext.Provider value={value}>{children}</BoltContext.Provider>
}

export const useBoltContext = () => useProvideContext(BoltContext, 'Bolt')
